import { functions } from "./firebase";

/* 
 * SECURE AI CLIENT
 * All API keys are now stored securely in Google Cloud Secrets.
 * The frontend only calls these proxied functions.
 */

/* Generates a helpful hint when a user is stuck on a puzzle */
export async function getAIGeneratedHint(puzzleId: string, originalClue: string, answer: string): Promise<string | null> {
    try {
        const getHint = functions.httpsCallable('getAIGeneratedHint');
        const result = await getHint({ puzzleId, originalClue, answer });
        return (result.data as any).hint;
    } catch (error: any) {
        console.error("Error getting AI hint:", error);
        return null;
    }
}

/* Generates a cryptic clue for a new puzzle based on an answer and optionally an image */
export async function getAIGeneratedClue(answer: string, imageBase64DataUrl?: string): Promise<string | null> {
    try {
        const getClue = functions.httpsCallable('getAIGeneratedClue');
        const result = await getClue({ answer, imageBase64: imageBase64DataUrl });
        return (result.data as any).clue;
    } catch (error: any) {
        console.error("Error getting AI clue:", error);
        return null;
    }
}

/* Generates a custom image based on a user description */
export async function generateAIImage(prompt: string): Promise<string> {
    try {
        const generateImage = functions.httpsCallable('generateAICustomImage');
        const result = await generateImage({ prompt });
        return (result.data as any).base64Image;
    } catch (error: any) {
        console.error("Error generating AI image:", error);
        throw error;
    }
}

/* Generates a support assistant response for technical issues or reporting */
export async function getAIChatbotResponse(
    history: { role: 'user' | 'model'; text: string }[],
    newMessage: string
): Promise<string | null> {
    try {
        const getChat = functions.httpsCallable('getAIChatbotResponse');
        const result = await getChat({ history, newMessage });
        return (result.data as any).text;
    } catch (error: any) {
        console.error("Error getting AI chatbot response:", error);
        return "I'm sorry, I'm having a little trouble connecting. Please try again in a moment.";
    }
}

/* Uses AI to analyze a chat transcript and extract key triage information */
export async function processSupportChatForTriage(transcript: { role: 'user' | 'model'; text: string }[]): Promise<any | null> {
    try {
        const processTriage = functions.httpsCallable('processSupportChatForTriage');
        const result = await processTriage({ transcript });
        return (result.data as any).json;
    } catch (error) {
        console.error("Error processing chat for triage:", error);
        return null;
    }
}

/* Converts text to speech using Gemini's native TTS capability */
export async function generateSpeechFromText(text: string): Promise<string | null> {
    try {
        const genSpeech = functions.httpsCallable('generateSpeechFromText');
        const result = await genSpeech({ text });
        return (result.data as any).audioBase64;
    } catch (error: any) {
        console.error("Error generating speech:", error);
        return null;
    }
}